/** A least-recently-used cache with item and byte caps (master §10.4). */

export interface Cache<V> {
  get(key: string): V | undefined;
  set(key: string, value: V): void;
  delete(key: string): void;
  clear(): void;
  readonly size: number;
  readonly bytes: number;
}

export interface CacheLimits<V> {
  readonly maxItems: number;
  readonly maxBytes: number;
  readonly sizeOf: (value: V) => number;
}

/** Create a cache; the oldest entries are evicted until both caps hold; an oversize value is not kept. */
export function createCache<V>(limits: CacheLimits<V>): Cache<V> {
  const entries = new Map<string, { value: V; bytes: number }>();
  let total = 0;
  const evict = (key: string): void => {
    const entry = entries.get(key);
    if (entry !== undefined) {
      total -= entry.bytes;
      entries.delete(key);
    }
  };
  return {
    get: (key) => {
      const entry = entries.get(key);
      if (entry === undefined) {
        return undefined;
      }
      entries.delete(key);
      entries.set(key, entry);
      return entry.value;
    },
    set: (key, value) => {
      evict(key);
      const bytes = limits.sizeOf(value);
      if (bytes > limits.maxBytes) {
        return;
      }
      entries.set(key, { value, bytes });
      total += bytes;
      for (const oldest of entries.keys()) {
        if (entries.size <= limits.maxItems && total <= limits.maxBytes) {
          break;
        }
        evict(oldest);
      }
    },
    delete: evict,
    clear: () => {
      entries.clear();
      total = 0;
    },
    get size() {
      return entries.size;
    },
    get bytes() {
      return total;
    },
  };
}
